import { motion } from 'framer-motion';
import { Heart, Copy, Share2, Download, Quote } from 'lucide-react';
import { useToggleFavorite } from '@/hooks/useFavorites';
import { useShare } from '@/hooks/useShare';

interface QuoteCardProps {
  quote: {
    id: number;
    text: string;
    author_name?: string;
    author_slug?: string;
    category_name?: string;
    category_slug?: string;
    is_favorited?: boolean;
  };
  isFavorited?: boolean;
  onDownload?: () => void;
}

export function QuoteCard({ quote, isFavorited = false, onDownload }: QuoteCardProps) {
  const toggleFavorite = useToggleFavorite();
  const { shareTwitter, copyToClipboard } = useShare();

  const handleCopy = async () => {
    await copyToClipboard(`"${quote.text}" — ${quote.author_name || 'Unknown'}`);
  };

  const handleShareText = `"${quote.text}" — ${quote.author_name || 'Unknown'}`;

  const handleToggleFavorite = () => {
    toggleFavorite.mutate({ quoteId: quote.id, isFavorited });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card group"
    >
      <div className="flex items-start gap-3 mb-4">
        <Quote size={24} className="text-primary-500 shrink-0 mt-1" />
        <p className="text-lg leading-relaxed">{quote.text}</p>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {quote.author_name && (
            <div>
              <p className="font-semibold text-sm">{quote.author_name}</p>
              {quote.category_name && (
                <p className="text-xs text-surface-500">{quote.category_name}</p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={handleToggleFavorite}
            className="p-2 rounded-full hover:bg-surface-100 transition-colors"
          >
            <Heart
              size={16}
              className={isFavorited ? 'fill-red-500 text-red-500' : 'text-surface-400'}
            />
          </button>
          <button
            onClick={handleCopy}
            className="p-2 rounded-full hover:bg-surface-100 transition-colors"
          >
            <Copy size={16} className="text-surface-400" />
          </button>
          <button
            onClick={() => shareTwitter(handleShareText)}
            className="p-2 rounded-full hover:bg-surface-100 transition-colors"
          >
            <Share2 size={16} className="text-surface-400" />
          </button>
          {onDownload && (
            <button
              onClick={onDownload}
              className="p-2 rounded-full hover:bg-surface-100 transition-colors"
            >
              <Download size={16} className="text-surface-400" />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
